import { createSlice } from "@reduxjs/toolkit";
import {
  EXCEPTION_ROOT,
  EXCEPTION_SESSION_EXPIRED,
  LOGOUT,
  SAML_SUCCESS,
} from "constants/routes";

const PUBLIC_PAGES = [
  SAML_SUCCESS,
  `${EXCEPTION_ROOT}/saml-error`,
  `${EXCEPTION_ROOT}/${EXCEPTION_SESSION_EXPIRED}`,
  LOGOUT,
];

export const onSpecialPage = () =>
  PUBLIC_PAGES.some((route) => window.location.hash.includes(route));

export const initialState = {
  hasUnsavedChanges: false,
  navBarAndSideBarDisabled: false,
  alerts: { global: null, inline: null },
  isInitialized: onSpecialPage(),
  isSidebarCollapsed: false,
  isSwitchingWorkspace: false,
  pageLoading: 0,
  jobQueue: [],
  actionRequired: { message: "", buttonContent: "", buttonAction: () => {} },
  session: null,
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    addToJobQueue: (state, action) => {
      state.jobQueue = state.jobQueue.concat(action.payload);
    },
    removeFromJobQueue: (state, action) => {
      state.jobQueue = state.jobQueue.filter((job) => job !== action.payload);
    },
    clearJobQueue: (state) => {
      state.jobQueue = [];
    },
    pageLoading: (state, action) => {
      // This does not need a queue (array of strings), this is a single threaded VM, this simple counter suffices
      // There are specialised queues for file uploading and comparison etc. job handling (different kinds of queues
      // and features with different operational dynamics which handle page loading modal backdrops in there own way)
      state.pageLoading += action.payload ? 1 : -1;
      if (state.pageLoading < 0) {
        console.error("Extra page unloading!");
        state.pageLoading = 0;
      }
    },
    disableNavBarAndSideBar: (state, action) => {
      state.navBarAndSideBarDisabled = action.payload;
    },
    setActionRequired: (state, action) => {
      state.actionRequired = action.payload;
    },
    showGlobalAlert: (state, action) => {
      state.alerts.global = action.payload;
    },
    setHasUnsavedChanges: (state, { payload }) => {
      state.hasUnsavedChanges = Boolean(payload);
    },
    hideGlobalAlert: (state) => {
      state.alerts.global = null;
    },
    showInlineAlert: (state, action) => {
      state.alerts.inline = action.payload;
    },
    hideInlineAlert: (state) => {
      state.alerts.inline = null;
    },
    currentSessionInfo: (state, action) => {
      state.session = { ...state.session, ...action.payload };
    },
    setIsSidebarCollapsed: (state, { payload }) => {
      state.isSidebarCollapsed = Boolean(payload);
    },
    renameSessionWorkspace: (
      state,
      { payload: { workspaceId, workspaceName } }
    ) => {
      const { userInfo } = state.session || {};
      if (!userInfo?.workspaces) {
        return;
      }
      const workspace = userInfo.workspaces.find(
        (w) => w.workspaceId === workspaceId
      );
      if (workspace) {
        workspace.workspaceName = workspaceName;
      }
      if (userInfo.workspaceId === workspaceId) {
        userInfo.workspaceName = workspaceName;
      }
    },
    setIsInitialized: (state, { payload }) => {
      state.isInitialized = Boolean(payload);
    },
    setIsSwitchingWorkspace: (state, { payload }) => {
      state.isSwitchingWorkspace = Boolean(payload);
    },
  },
});

export const {
  pageLoading,
  addToJobQueue,
  removeFromJobQueue,
  clearJobQueue,
  disableNavBarAndSideBar,
  setActionRequired,
  setHasUnsavedChanges,
  showGlobalAlert,
  hideGlobalAlert,
  showInlineAlert,
  hideInlineAlert,
  currentSessionInfo,
  setIsSidebarCollapsed,
  renameSessionWorkspace,
  setIsInitialized,
  setIsSwitchingWorkspace,
} = appSlice.actions;

export default appSlice.reducer;
